/** How the booking record's read-only (vendor / derived) fields render, and the value
 *  actions of its editable ones — one module, so a value that names another record offers
 *  the same links on every surface (bookingLinks).
 *
 *  - `fieldDisplay(key, c)` is the `display` a FieldSlot shows for a vendor / derived key:
 *    what the panel reads from the detail or edit_context (catalog `detail_keys` /
 *    `context_keys`). `null` renders the em dash; `undefined` = no special rendering.
 *  - `editableDisplay(key, c)` is the view-mode rendering of an editable field whose
 *    stored value carries its own links (the tag chips).
 *  - `fieldActions(key, c)` builds the value actions of an editable field from its LIVE
 *    value (FieldSlot puts them in the input's adornment or a links row in edit mode).
 *
 *  Times are always in the tour's zone and labelled (`formatZoned`); the UTC start says
 *  UTC. */
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Box, Link, Stack } from '@mui/material';
import type { ReactNode } from 'react';

import type { BookingRecordEditContext } from '@/api/bookingRecord';
import type { BookingDetail } from '@/api/bookings';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { formatZoned, formatZonedRange } from '@/utils/zonedTime';

import {
  affiliateActions,
  bookingSystemActions,
  customIdActions,
  dateActions,
  departureKeyActions,
  integrationActions,
  orderActions,
  paxActions,
  refActions,
  registrationMethodActions,
  restechAccountActions,
  timestampActions,
  tourActions,
  type BookingCtx,
} from '../../overview/bookingLinks';
import { CopyableValue, formatMoney, ShortLink } from '../../overview/common';

export interface BookingDisplayContext {
  detail: BookingDetail;
  editContext: BookingRecordEditContext;
  /** The tour's IANA zone. */
  tz: string;
  links: BookingCtx;
}

/** What the panels of either view render values with. */
export function bookingDisplayContext(detail: BookingDetail, editContext: BookingRecordEditContext): BookingDisplayContext {
  return {
    detail,
    editContext,
    tz: editContext.schedule.timezone,
    links: { tenantId: detail.tenant?.id ?? editContext.tenant.id },
  };
}

export const MONO_SX = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13 } as const;

function Mono({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={MONO_SX}>
      {children}
    </Box>
  );
}

function withActions(node: ReactNode, actions: ValueAction[]): ReactNode {
  return actions.length > 0 ? <ValueActions actions={actions}>{node}</ValueActions> : node;
}

const yesNo = (v: boolean | null | undefined) => (v === null || v === undefined ? null : v ? 'Yes' : 'No');

function money(minor: number | null | undefined, d: BookingDetail): string | null {
  return formatMoney(minor ?? null, d.currency, d.currency_precision);
}

/** "Retail 387.00 GBP · Net 309.60 GBP · Tax 64.50 GBP" — only the amounts sent. */
function pricing(d: BookingDetail): ReactNode {
  const parts = (
    [
      ['Retail', d.price_retail],
      ['Original', d.price_original !== d.price_retail ? d.price_original : null],
      ['Net', d.price_net],
      ['Tax', d.tax_amount],
    ] as const
  )
    .map(([label, v]) => {
      const m = money(v, d);
      return m ? `${label} ${m}` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function ticketSummary(d: BookingDetail): string | null {
  const own = d.tickets.filter((t) => !t.inherited);
  const shown = own.length > 0 ? own : d.tickets;
  if (shown.length === 0) return null;
  const redeemed = shown.filter((t) => t.redeemed_at).length;
  const noShow = shown.filter((t) => t.noshowed_at).length;
  return [
    `${shown.length} ${shown.length === 1 ? 'ticket' : 'tickets'}`,
    `${redeemed} redeemed`,
    noShow ? `${noShow} no-show` : null,
    own.length === 0 ? 'from the main booking' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const EXCLUSION_LABELS: Record<string, string> = {
  booking_state: 'No — the booking is not Booked',
  staff_role: 'No — a staff booking',
  party_member: 'No — party members are hidden at check-in',
  access_group: 'No — the guest’s access group',
};

/** "Sat 3 Oct 07:30 – no end Europe/London · open now" — the check-in window, labelled. */
export function checkinWindowText(checkin: BookingRecordEditContext['checkin'], tz: string): string | null {
  const w = checkin.window;
  if (!w.opens_ts && !w.closes_ts) return w.is_open ? 'Open' : null;
  const range = formatZonedRange(w.opens_ts, w.closes_ts, tz, { collapse: false, openStart: 'any time', openEnd: 'no end' });
  return `${range}${w.is_open ? ' · open now' : ''}`;
}

/** Whether the booking is in its departure's guide check-in list, and why not. */
export function inManifestText(checkin: BookingRecordEditContext['checkin']): string {
  return checkin.in_manifest ? 'Yes' : (EXCLUSION_LABELS[checkin.manifest_exclusion ?? ''] ?? 'No');
}

function platformText(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.map(String).join(', ') || null;
  if (typeof v === 'object') return Object.keys(v as object).join(', ') || null;
  return String(v);
}

/** The read-only rendering of a vendor / derived field. */
export function fieldDisplay(key: string, c: BookingDisplayContext): ReactNode | undefined {
  const { detail: d, editContext: ctx, tz, links } = c;
  const when = (iso: string | null | undefined) => (iso ? formatZoned(iso, tz) : null);
  switch (key) {
    // ---- Booked & order
    case 'custom_id':
      return d.custom_id ? withActions(<Mono>{d.custom_id}</Mono>, customIdActions(d.custom_id, links)) : null;
    case 'product_custom_id':
      return d.product_custom_id ? <CopyableValue value={d.product_custom_id} label="Product ID" /> : null;
    case 'prev_custom_id':
      return d.prev_custom_id ? <CopyableValue value={d.prev_custom_id} label="Previous booking ID" /> : null;
    case 'public_id':
      return d.public_id ? <CopyableValue value={d.public_id} label="Public ID" /> : null;
    case 'booking_ts':
      return when(d.booking_ts);
    case 'pricing':
      return pricing(d);
    case 'promo': {
      if (!d.promo_code) return null;
      const discount = money(d.promo_discount, d);
      return (
        <>
          <Mono>{d.promo_code}</Mono>
          {discount ? ` · −${discount}` : ''}
        </>
      );
    }
    case 'settlement_method':
      return d.settlement_method || null;
    case 'email_receipt':
      return yesNo(d.email_receipt);
    case 'tickets':
      return ticketSummary(d);
    case 'extras': {
      const extras = d.extras ?? [];
      if (extras.length === 0) return null;
      return (
        <Stack spacing={0.25} component="span">
          {extras.map((x) => {
            const price = money(x.price_retail, { ...d, currency: x.currency ?? d.currency, currency_precision: x.currency_precision ?? d.currency_precision });
            return (
              <span key={x.id}>
                {x.quantity}× {x.title}
                {price ? ` · ${price}` : ''}
              </span>
            );
          })}
        </Stack>
      );
    }
    case 'voucher':
      return d.voucher ? <ShortLink att={d.voucher} /> : null;
    case 'invoice':
      return d.invoice ? <ShortLink att={d.invoice} /> : null;
    case 'attachments':
      return d.attachments.length ? (
        <Stack spacing={0.25} component="span">
          {d.attachments.map((a, i) => (
            // Attachments carry no id; the list is static per render.
            // eslint-disable-next-line react/no-array-index-key
            <ShortLink key={i} att={a} />
          ))}
        </Stack>
      ) : null;
    case 'integration':
      return d.integration ? withActions(d.integration, integrationActions(d.integration, links)) : null;
    case 'restech_account':
      return d.restech_account ? withActions(<Mono>{d.restech_account}</Mono>, restechAccountActions(d.restech_account, links)) : null;
    case 'booking_system':
      return d.booking_system_display
        ? withActions(d.booking_system_display, bookingSystemActions(d.booking_system_display, links))
        : null;
    case 'create_source':
      return d.create_source_display
        ? withActions(d.create_source_display, registrationMethodActions(d.create_source_display, links))
        : null;
    case 'update_source':
      return d.update_source_display || null;
    case 'created_ts':
      return d.created_ts
        ? withActions(when(d.created_ts), timestampActions('created', d.created_ts, d.create_source_display, links))
        : null;
    case 'updated_ts':
      return d.updated_ts
        ? withActions(when(d.updated_ts), timestampActions('updated', d.updated_ts, d.update_source_display, links))
        : null;

    // ---- Guest & pre-check-in
    case 'ota':
      return d.guest?.ota?.name ? [d.guest.ota.name, d.guest.ota.domain].filter(Boolean).join(' · ') : null;
    case 'email_verified':
      return yesNo(ctx.guest?.email_verified);
    case 'email_is_fake':
      return yesNo(ctx.guest?.email_is_fake);
    case 'app_platform':
      return platformText(d.guest?.platform);
    case 'twilio_identity':
      return d.guest?.twilio_identity ? <CopyableValue value={d.guest.twilio_identity} label="Chat identity" /> : null;

    // ---- Departure
    case 'all_day':
      return yesNo(d.all_day);
    case 'start_ts':
      return d.start_ts ? formatZoned(d.start_ts, 'UTC') : null;
    case 'timezone':
      return tz;
    case 'is_bespoke':
      return yesNo(d.tour?.is_bespoke ?? null);
    case 'departure':
      return d.departure?.name ?? null;

    // ---- On-site
    case 'ticket_redemption': {
      const own = d.tickets.filter((t) => !t.inherited);
      const shown = own.length ? own : d.tickets;
      if (!shown.length) return null;
      return `${shown.filter((t) => t.redeemed_at).length} of ${shown.length} redeemed`;
    }
    case 'checkin_source':
      return d.checkin_ts || d.checkin_status === 'checked_in' ? d.checkin_source_display || null : null;
    case 'checkin_ts':
      return when(d.checkin_ts);
    case 'checkin_error':
      return d.checkin_error || null;
    case 'checkin_url':
      return d.checkin_url ? (
        <Link href={d.checkin_url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all' }}>
          {d.checkin_url}
        </Link>
      ) : null;
    case 'checkin_window':
      return checkinWindowText(ctx.checkin, tz);
    case 'in_manifest':
      return inManifestText(ctx.checkin);

    // ---- After trip
    case 'reviews': {
      const r = d.reviews;
      if (!r || r.items.length === 0) return r?.available ? 'No review yet' : null;
      return (
        <Stack spacing={0.25} component="span">
          {r.items.map((rv) => (
            <span key={rv.id}>
              {rv.star_rating != null ? `${rv.star_rating}★` : 'Review'}
              {rv.created_ts ? ` · ${formatZoned(rv.created_ts, tz, { time: false, zone: false })}` : ''}
              {rv.comment ? ` · “${rv.comment}”` : ''}
            </span>
          ))}
        </Stack>
      );
    }
    default:
      return undefined;
  }
}

/** The view-mode rendering of an editable field whose stored value names records with
 *  their own links — the booking's tags, each chip with its actions (open the tag, filter
 *  by it). Only the stored tags of the field (its baseline ids), never a draft value. */
export function editableDisplay(key: string, c: BookingDisplayContext): ReactNode | undefined {
  if (key !== 'tags' && key !== 'precheckin_tags') return undefined;
  const ids = c.editContext.baselines[`booking:${c.editContext.booking_id}`]?.[key];
  if (!Array.isArray(ids)) return undefined;
  const stored = new Set(ids);
  const tags = c.detail.tags.filter((t) => stored.has(t.id));
  if (tags.length === 0) return null;
  return (
    <TagBadgeSet
      tags={tags}
      maxVisible={12}
      chipActions={(t) => tagChipActions(t, { tenantId: c.links.tenantId, owner: 'booking' })}
    />
  );
}

/** A guest field edit_context left out for this caller (`guest.omitted_fields` — the
 *  guest's PII without console_user_view): it reads as hidden, never as empty, and
 *  offers no input (editorConfig `editabilityOf`). */
export function omittedDisplay(
  f: { entity: string; id: number; key: string },
  c: BookingDisplayContext,
): ReactNode | undefined {
  const guest = c.editContext.guest;
  if (f.entity !== 'user' || !guest || f.id !== guest.user_id || !guest.omitted_fields.includes(f.key)) {
    return undefined;
  }
  return (
    <Box
      component="span"
      data-hidden-value
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', color: 'text.secondary' }}
    >
      <VisibilityOffOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
      Hidden · needs console_user_view
    </Box>
  );
}

/** Value actions of an editable field, built from its live value. */
export function fieldActions(key: string, c: BookingDisplayContext): ((value: unknown) => ValueAction[]) | undefined {
  const { detail: d, links } = c;
  const str = (fn: (v: string) => ValueAction[]) => (v: unknown) =>
    typeof v === 'string' && v.trim() ? fn(v.trim()) : [];
  switch (key) {
    case 'order_ref':
      return str((v) => orderActions({ order_id: v === d.order_ref ? d.order_id : null, order_ref: v }, links));
    case 'supplier_ref':
      return str((v) => refActions('supplier', v, links));
    case 'reseller_ref':
      return str((v) => refActions('reseller', v, links));
    case 'affiliate_ext_id':
      return str((v) => affiliateActions(v, links));
    case 'departure_key':
      return str((v) => departureKeyActions(v, links));
    case 'date':
      return str((v) => dateActions(v, links));
    case 'tour':
      return (v) => (typeof v === 'number' ? tourActions({ id: v }, links) : []);
    case 'people_count':
      return (v) => (typeof v === 'number' ? paxActions(v, links) : []);
    case 'package_id':
      return str((v) => [act.copy(v, 'Copy package id')]);
    default:
      return undefined;
  }
}
